import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, DatePicker, Form, Input, Modal, Table } from 'antd'
import dayjs from 'dayjs'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useTRPC } from '@/trpc/react.ts'

export function EndRental({ order, onClose }: { order: RentalOrder; onClose: () => void }) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp(),
    [date, setDate] = useState(() => dayjs(order.endAt)),
    [reason, setReason] = useState('')
  const input = { rentalOrderId: order.id, date: date.format('YYYY-MM-DD') },
    preview = useQuery(trpc.order.rental.endPreview.queryOptions(input)),
    save = useMutation(trpc.order.rental.end.mutationOptions())
  return (
    <Modal
      open={open}
      title="结束租赁与尾期核对"
      width="60rem"
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      confirmLoading={save.isPending}
      closable={!save.isPending}
      keyboard={!save.isPending}
      mask={{ closable: false }}
      cancelButtonProps={{ disabled: save.isPending }}
      okText="确认截止日期"
      okButtonProps={{
        danger: true,
        disabled: !reason.trim() || !preview.data || preview.isFetching || preview.isError
      }}
      onOk={async () => {
        try {
          await save.mutateAsync({ ...input, reason, expectedUpdatedAt: preview.data!.updatedAt })
          await refreshMoneyQueries(cache, trpc)
          setOpen(false)
          void message.success('已确定租赁截止日期，请继续核对尾费和押金')
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '结束失败')
        }
      }}
    >
      <Alert
        className="mb-4!"
        type="warning"
        title="截止日不计费，未来截止只缩短资源占用期间；退款需要另外登记真实资金。"
      />
      <Form layout="vertical" disabled={save.isPending}>
        <Form.Item label="租赁截止日（不含）" required>
          <DatePicker
            value={date}
            allowClear={false}
            onChange={(v) => {
              if (v) {
                setDate(v)
              }
            }}
          />
        </Form.Item>
        <Form.Item label="结束原因" required>
          <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
        </Form.Item>
      </Form>
      {preview.isError && <Alert type="error" title={preview.error.message} />}
      <Table
        rowKey="id"
        loading={preview.isFetching}
        pagination={false}
        dataSource={preview.data?.affected ?? []}
        columns={[
          { title: '账单', dataIndex: 'billNo' },
          { title: '有效金额', dataIndex: 'effective' },
          { title: '待收付', dataIndex: 'outstanding' },
          { title: '后续处理', dataIndex: 'action' }
        ]}
      />
    </Modal>
  )
}
